import { memo, useEffect, useRef, useState } from 'react';
import type { DeckSlide } from '../types';
import { DeckSlideView } from './slide';
import { SLIDE } from './frame';

// A slide is scaled, never reflowed. It is drawn at 1280x720 — the shape the
// .pptx will be — and one transform fits it to whatever width it has been
// given, so the canvas, the filmstrip thumbnail and the full-screen view are
// the same slide at three sizes rather than three responsive layouts.
//
// The scaled slide is absolutely positioned, and that is load-bearing: in
// normal flow a 1280px-wide child contributes 1280px of min-content width to
// every flex ancestor, so a 700px panel measures itself as far wider and the
// stage scales to fit a width it caused. Out of flow it measures nothing, which
// is why `.deck-stage` carries the aspect ratio that reserves the height.
function Stage({ slide, label }: { slide: DeckSlide; label?: string }) {
  const frame = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useEffect(() => {
    const node = frame.current;
    if (!node) return;
    const measure = () => setScale(node.clientWidth / SLIDE.width);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <div className="deck-stage" ref={frame} role="img" aria-label={label}>
      {/* Held back until the first measurement so the slide never flashes at
          full size inside a narrow panel. */}
      {scale > 0 && (
        <div style={{ transform: `scale(${scale})` }}>
          <DeckSlideView slide={slide} />
        </div>
      )}
    </div>
  );
}

// Memoized because the filmstrip holds one stage per slide: stepping through a
// forty-slide deck would otherwise re-render forty slides per key press.
export const DeckStage = memo(Stage);
